import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditPir } from '@/api/DTO/family/family.interface';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { SEX_LIST, YESNO } from '@/common/constants/common';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useParams } from 'react-router-dom';
import {
	useGetFamilyDetailQuery,
	useGetFamilyGroupListMutation,
	useUpdatePirMutation,
} from '@/api/family-management/familyApi';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetAllEnumListQuery, useGetDicListQuery } from '@/api/common';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import {
	API_DATE_FORMAT,
	DISPLAY_DATE_FORMAT,
	notFutureDate,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import SelectMemberEnDialog from './components/SelectMemberEnDialog';
export default function FamilyEditRecoveryPage() {
	const { t } = useTranslation();
	const { familyId } = useParams();
	const navigate = useNavigate();
	const [updatePir] = useUpdatePirMutation();
	const [memberList, setMemberList] = useState<any>([]);
	const [getFamilyGroupList] = useGetFamilyGroupListMutation();

	const { data: details } = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {
		pirType: null,
		pirSex: null,
		liveWithPir: null,
	};

	const formSchema = Yup.object().shape({
		pirType: Yup.string().required(t('errorMessage.pleaseSelect')),

		pirNameZh: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		pirSex: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		pirDateOfBirth: Yup.string()
			.when('pirType', {
				is: (pirType: number) => pirType == 1,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable(),
			})
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		relationshipToPir: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		liveWithPir: Yup.string().when('pirType', {
			is: (pirType: number) => pirType == 1,
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchPirType = methods.watch('pirType');

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
		}
	}, [details]);

	const {
		fields: familyGroupFields,
		append: familyGroupAppend,
		remove: familyGroupRemove,
		update: familyGroupUpdate,
	}: any = useFieldArray({
		control: methods.control,
		name: 'familyMemberApplicationRelationshipList',
	});

	const onSubmit: SubmitHandler<EditPir> = async (data) => {
		const response = await updatePir(data);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFamilySuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		navigate(`/family-management/family-management-list/detail/${familyId}`);
	};

	const getSelectMemberRow = async (row: any) => {
		setMemberList([row]);

		const response = await getFamilyGroupList(row.id);
		methods.setValue(
			'familyMemberApplicationRelationshipList',
			response?.data?.data || []
		);
	};

	const columns: GridColDef[] = [
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'sexDescZh',
			headerName: t('preAdmission.sex'),
			flex: 15,
		},
		{
			field: 'pirDateOfBirth',
			headerName: t('preAdmission.dateOfBirth'),
			flex: 15,
			renderCell: (params) =>
				!params.row.pirDateOfBirth
					? '-'
					: dayjs(params.row.pirDateOfBirth)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
		},
		{
			field: 'branchDisplayName',
			headerName: t('member.unit'),
			flex: 15,
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyFileNo'),
			flex: 15,
		},
		{
			field: 'relationshipToPir',
			headerName: t('family.relationshipToPIR'),
			flex: 15,
			renderCell: (params) => (
				<AppDropDownMenu
					optional={true}
					control={methods.control}
					name='relationshipToPir'
					label={t('family.relationshipToPIR')}
					options={(relationshipDicList?.data || []).map(
						(item: { labelEn: any }) => {
							return {
								...item,
								name: item.labelEn,
							};
						}
					)}
				/>
			),
		},
		{
			field: 'relationshipToApplicant',
			headerName: t('family.relationshipToApp'),
			flex: 15,
			renderCell: (params) => (
				<AppDropDownMenu
					optional={true}
					control={methods.control}
					name='relationshipToApplicant'
					label={t('family.relationshipToApp')}
					options={(relationshipDicList?.data || []).map(
						(item: { labelEn: any }) => {
							return {
								...item,
								name: item.labelEn,
							};
						}
					)}
				/>
			),
		},
		{
			field: 'liveWithPir',
			headerName: t('family.livingPIR'),
			flex: 15,
			renderCell: (params) => (
				<AppDropDownMenu
					optional={true}
					control={methods.control}
					name='liveWithPir'
					label={t('family.liveWithPir')}
					options={YESNO}
				/>
			),
		},
	];
	const columns2: GridColDef[] = [
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>
						{params.row.targetMemberNameEn}
					</Typography>
					<Typography variant='body2'>
						{params.row.targetMemberNameZh}
					</Typography>
				</Box>
			),
		},
		{
			field: 'targetMemberSexDescZh',
			headerName: t('preAdmission.sex'),
			flex: 15,
		},
		{
			field: 'targetMemberDateOfBirth',
			headerName: t('preAdmission.dateOfBirth'),
			flex: 15,
			renderCell: (params) =>
				!params.row.targetMemberDateOfBirth
					? '-'
					: dayjs(params.row.targetMemberDateOfBirth)
							.locale('en')
							.format(DISPLAY_DATE_FORMAT),
		},
		{
			field: 'targetMemberBranchDisplayName',
			headerName: t('preAdmission.unit'),
			flex: 15,
		},
		{
			field: 'targetMemberFileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyFileNo'),
			flex: 15,
		},
		{
			field: 'relationshipToPir',
			headerName: t('family.relationshipToPIR'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods.control}
						name={`familyMemberApplicationRelationshipList[${index}].relationshipToPir`}
						label={t('family.relationshipToPIR')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},

		{
			field: 'relationshipToApplicant',
			headerName: t('family.relationshipToApp'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods.control}
						name={`familyMemberApplicationRelationshipList[${index}].relationshipToApplicant`}
						label={t('family.relationshipToApp')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box>
					<Typography variant='h4'>
						{t('drawerListItems.editFamilyMember')}
					</Typography>
					<Card sx={{ marginTop: 3 }}>
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<CardContent sx={{ padding: '24px' }}>
									<Typography
										variant='h6'
										sx={{ fontWeight: 'bold' }}
										marginBottom={2}
									>
										{t('family.personRecoveryInformation')}
									</Typography>
									<Grid
										container
										spacing={3}
										marginTop={0}
										marginBottom={3}
										alignItems='center'
									>
										<Grid size={6}>
											<Typography variant='body2' color='common.neutral40'>
												{t('family.isPIR') + '*'}
											</Typography>
											<AppRadioGroup
												disabled={true}
												control={methods.control}
												name={`pirType`}
												row
												options={(allEnumList?.data.PIRTypeEnum || []).map(
													(item: any) => {
														return {
															...item,
															id: item.code,
															name: item.desc,
														};
													}
												)}
												handleChange={() => {
													methods.setValue('pirNameZh', '');
													methods.setValue('pirSex', '');
													methods.setValue('pirDateOfBirth', '');
													methods.setValue('relationshipToPir', '');
													methods.setValue('liveWithPir', '');

													methods.clearErrors('pirNameZh');
													methods.clearErrors('pirSex');
													methods.clearErrors('pirDateOfBirth');
													methods.clearErrors('relationshipToPir');
													methods.clearErrors('liveWithPir');
												}}
											/>
										</Grid>
										<Grid size={1}></Grid>
										{watchPirType === '0' && (
											<>
												<Grid
													size={5}
													display={'flex'}
													justifyContent={'flex-end'}
												>
													<SelectMemberEnDialog
														getSelectMemberRow={getSelectMemberRow}
													/>
												</Grid>
												<Grid size={12} display='flex'>
													<LocalDataGrid
														rows={memberList}
														columns={columns}
														getRowId={(row) => row.id}
														pagination={false}
													/>
												</Grid>
												<Grid size={12}>
													<Typography variant='body1'>
														{t('family.otherFamilyMembers')}
													</Typography>
												</Grid>
												<Grid size={12} display='flex'>
													<LocalDataGrid
														rows={familyGroupFields.map(
															(item: any, index: any) => ({
																...item,
																index,
															})
														)}
														columns={columns2}
														getRowId={(row) => row.id}
														pagination={false}
													/>
												</Grid>
											</>
										)}

										{watchPirType == 1 && (
											<>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name='pirNameZh'
														label={t('preAdmission.name_zh') + '*'}
													/>
												</Grid>
												<Grid size={6}>
													<Typography variant='body2' color='common.neutral40'>
														{t('preAdmission.sex') + '*'}
													</Typography>
													<AppRadioGroup
														control={methods.control}
														name='pirSex'
														row
														options={SEX_LIST}
													/>
												</Grid>
												<Grid size={6}>
													<AppDatePicker
														maximumDate={dayjs()
															.startOf('day')
															.format(API_DATE_FORMAT)}
														control={methods.control}
														name='pirDateOfBirth'
														label={t('preAdmission.dateOfBirth') + '*'}
													/>
												</Grid>
												<Grid size={6}></Grid>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name='pirUnit'
														label={t('member.unit')}
													/>
												</Grid>
												<Grid size={6}>
													<AppTextField
														control={methods.control}
														name='pirFileNo'
														label={t('member.fileNo')}
													/>
												</Grid>
												<Grid size={6}>
													<AppDropDownMenu
														optional={true}
														control={methods.control}
														name='relationshipToPir'
														label={t('family.relationshipToPIR') + '*'}
														options={(relationshipDicList?.data || []).map(
															(item: { labelEn: any }) => {
																return {
																	...item,
																	name: item.labelEn,
																};
															}
														)}
													/>
												</Grid>
												<Grid size={6}>
													<Typography variant='body2' color='common.neutral40'>
														{t('family.livingPIR') + '*'}
													</Typography>
													<AppRadioGroup
														control={methods.control}
														name='liveWithPir'
														row
														options={YESNO}
													/>
												</Grid>
											</>
										)}
									</Grid>
								</CardContent>
								<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
									<Button onClick={handleClose}>{t('button.cancel')}</Button>
									<Button
										type='submit'
										variant='contained'
										endIcon={<CheckCircleOutlineIcon />}
									>
										{t('button.complete')}
									</Button>
								</CardActions>
							</form>
						</FormProvider>
					</Card>
				</Box>
			</Box>
		</>
	);
}
